import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MuiAlert from '../utils/muiAlerts';
import { getCachePolicySettings, saveCachePolicySettings } from '../utils/getApis';
import { queryKeys } from './queryKeys';
import { invalidateCacheByType, updateServiceWorkerCachePolicy } from '../utils/swCacheUtils';
import { updateCachePolicy, broadcastPolicyToServiceWorker } from '../utils/cachePolicyManager';

export const useCachePolicySettings = (currentUserInfo) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.cachePolicySettings,
    queryFn: () => getCachePolicySettings(currentUserInfo),
    enabled: Boolean(currentUserInfo?.uid),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (payload) => saveCachePolicySettings(currentUserInfo, payload),
    onSuccess: async (data) => {
      // 1. Update cachePolicyManager (syncs with queryClient automatically)
      updateCachePolicy(data);
      console.log('[CachePolicySettings] Cache policy updated in manager');

      // 2. Update Service Worker with new TTL values
      try {
        await updateServiceWorkerCachePolicy(data);
        console.log('[CachePolicySettings] Service Worker cache policy updated');
      } catch (error) {
        console.warn('Failed to update Service Worker cache policy:', error);
      }

      // 3. Broadcast to Service Worker for sync
      try {
        await broadcastPolicyToServiceWorker(data);
      } catch (error) {
        console.warn('Failed to broadcast policy to Service Worker:', error);
      }

      // 4. Invalidate Service Worker cache for cache policy settings
      try {
        await invalidateCacheByType('cachePolicySettings');
      } catch (error) {
        console.warn('Failed to invalidate SW cache for cachePolicySettings:', error);
      }
      
      // 5. Invalidate TanStack Query cache
      queryClient.setQueryData(queryKeys.cachePolicySettings, data);
      queryClient.invalidateQueries({ queryKey: queryKeys.cachePolicySettings });
      MuiAlert.fire('Success!', 'Cache policy updated', 'success');
    },
    onError: (error) => {
      MuiAlert.fire('Error', error?.message || 'Failed to save cache policy', 'error');
    },
  });

  return {
    ...query,
    saveCachePolicy: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
};
